<template>
  <div class="container-fluid pb-4">

    <!-- ── Hero banner ── -->
    <div
      class="profile-banner border-radius-xl mt-4"
      :style="{ backgroundImage: 'url(' + require('@/assets/img/curved-images/curved14.jpg') + ')' }"
    >
      <span class="mask bg-gradient-success opacity-6"></span>
    </div>

    <!-- ── Identity bar ── -->
    <div class="identity-bar mx-4">
      <div class="identity-avatar">
        <img v-if="user.picture" :src="user.picture" :alt="user.name" referrerpolicy="no-referrer" />
        <div v-else class="avatar-initials">{{ initials }}</div>
      </div>
      <div class="identity-info">
        <h5 class="identity-name">{{ displayFullName }}</h5>
        <div class="identity-roles">
          <span
            v-for="r in profileRoles"
            :key="r.roleId"
            class="identity-role me-1 mb-1"
            :style="roleStyle(r.roleId, r.roleName)"
          >{{ (r.roleName || roleLabel(r.roleId)) }}</span>
        </div>
      </div>
    </div>

    <!-- ── Content ── -->
    <div class="row mt-4 g-3">

      <!-- Left: Profile info -->
      <div class="col-12 col-lg-7">
        <div class="card h-100">
          <div class="card-header d-flex align-items-center justify-content-between">
            <div>
              <h6 class="mb-0">Profile Information</h6>
              <p class="text-secondary text-xs mb-0">Personal details and account info</p>
            </div>
            <button class="pf-btn-edit" @click="openEdit">
              <i class="fas fa-pen me-1"></i> Edit
            </button>
          </div>
          <div class="card-body p-0">
            <div class="pf-row">
              <div class="pf-cell">
                <span class="pf-label">First Name</span>
                <span class="pf-value">{{ user.firstName || '—' }}</span>
              </div>
              <div class="pf-cell">
                <span class="pf-label">Last Name</span>
                <span class="pf-value">{{ user.lastName || '—' }}</span>
              </div>
            </div>
            <div class="pf-row">
              <div class="pf-cell">
                <span class="pf-label">Email / Username</span>
                <span class="pf-value">{{ user.email || '—' }}</span>
              </div>
              <div class="pf-cell">
                <span class="pf-label">Nickname</span>
                <span class="pf-value">{{ user.nickname || '—' }}</span>
              </div>
            </div>
            <div class="pf-row">
              <div class="pf-cell">
                <span class="pf-label">Team</span>
                <span class="pf-value">{{ formattedTeam }}</span>
              </div>
              <div class="pf-cell"></div>
            </div>
            <div class="pf-row border-bottom-0">
              <div class="pf-cell">
                <span class="pf-label">Roles</span>
                <span class="pf-value d-flex flex-wrap gap-1">
                  <template v-if="profileRoles.length">
                    <span v-for="r in profileRoles" :key="r.roleId" class="role-pill" :style="roleStyle(r.roleId, r.roleName)">{{ r.roleName || roleLabel(r.roleId) }}</span>
                  </template>
                  <span v-else>—</span>
                </span>
              </div>
              <div class="pf-cell">
                <span class="pf-label">Login method</span>
                <span class="pf-value">
                  <span v-if="user.googleId" class="method-badge google"><i class="fab fa-google me-1"></i>Google</span>
                  <span v-else class="method-badge password"><i class="fas fa-lock me-1"></i>Password</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Connected accounts -->
      <div class="col-12 col-lg-5">
        <div class="card h-100">
          <div class="card-header">
            <h6 class="mb-0">Connected Accounts</h6>
            <p class="text-secondary text-xs mb-0">Link your external accounts</p>
          </div>
          <div class="card-body p-0">

            <!-- Google -->
            <div class="connect-row">
              <div class="connect-icon google-icon">
                <i class="fab fa-google"></i>
              </div>
              <div class="connect-info">
                <span class="connect-name">Google</span>
                <span v-if="user.googleId" class="connect-status connected">Connected</span>
                <span v-else class="connect-status">Not connected</span>
              </div>
              <div class="connect-action">
                <span v-if="user.googleId" class="connect-check"><i class="fas fa-check-circle"></i></span>
                <button v-else class="connect-btn" :disabled="googleConnectLoading" @click="startConnectGoogle">
                  <span v-if="googleConnectLoading"><i class="fas fa-spinner fa-spin"></i></span>
                  <span v-else>Connect</span>
                </button>
              </div>
            </div>

            <!-- Jira -->
            <div class="connect-row border-bottom-0">
              <div class="connect-icon jira-icon">
                <i class="fab fa-jira"></i>
              </div>
              <div class="connect-info">
                <span class="connect-name">Jira</span>
                <template v-if="user.jiraAccountId">
                  <span class="connect-status connected">Connected</span>
                  <span class="connect-account-id" :title="user.jiraAccountId">{{ user.jiraAccountId }}</span>
                </template>
                <span v-else class="connect-status">Not connected</span>
              </div>
              <div class="connect-action">
                <button
                  v-if="user.jiraAccountId"
                  type="button"
                  class="connect-btn connect-btn-disconnect"
                  :disabled="jiraDisconnectLoading"
                  @click="disconnectJira"
                >
                  <span v-if="jiraDisconnectLoading"><i class="fas fa-spinner fa-spin"></i></span>
                  <span v-else>Disconnect</span>
                </button>
                <button
                  v-else
                  type="button"
                  class="connect-btn"
                  :disabled="jiraConnectLoading"
                  @click="startConnectJira"
                >
                  <span v-if="jiraConnectLoading"><i class="fas fa-spinner fa-spin"></i></span>
                  <span v-else>Connect</span>
                </button>
              </div>
            </div>
            <div v-if="jiraInlineError || (!user.jiraAccountId && jiraInlineSuccess)" class="connect-row-meta">
              <p v-if="jiraInlineError" class="connect-inline-error mb-0">
                <i class="fas fa-exclamation-circle me-1"></i>{{ jiraInlineError }}
              </p>
              <p v-else-if="jiraInlineSuccess" class="connect-inline-success mb-0">
                <i class="fas fa-check-circle me-1"></i>{{ jiraInlineSuccess }}
              </p>
            </div>
            <div v-if="!user.jiraAccountId" class="connect-row-meta">
              <button
                type="button"
                class="btn btn-link btn-sm p-0 jira-manual-link"
                @click="openJiraManualModal"
              >
                Enter Account ID manually
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- ── Edit Profile Modal ── -->
    <div v-if="showEdit" class="modal-overlay">
      <div class="pf-modal">
        <div class="pf-modal-header">
          <h6>Edit Profile</h6>
        </div>
        <div class="pf-modal-body">
          <div class="modal-row">
            <div class="modal-field">
              <label>First Name <span class="text-danger">*</span></label>
              <input type="text" v-model="form.firstName" class="form-control form-control-sm" placeholder="First name" />
            </div>
            <div class="modal-field">
              <label>Last Name <span class="text-danger">*</span></label>
              <input type="text" v-model="form.lastName" class="form-control form-control-sm" placeholder="Last name" />
            </div>
          </div>
          <div class="modal-field">
            <label>Email <span class="text-danger">*</span></label>
            <input type="email" v-model="form.email" class="form-control form-control-sm" placeholder="email@example.com" />
          </div>
          <div class="modal-field">
            <label>Nickname</label>
            <input type="text" v-model="form.nickname" class="form-control form-control-sm" placeholder="Nickname (optional)" />
          </div>
          <p class="text-secondary text-xs mb-0">To change your password, enter your current password and a new one.</p>
          <div class="modal-field">
            <label>Current password <span class="text-secondary fw-normal">(required if changing password)</span></label>
            <input type="password" v-model="form.currentPassword" class="form-control form-control-sm" placeholder="Current password" autocomplete="current-password" />
          </div>
          <div class="modal-field">
            <label>New password <span class="text-secondary fw-normal">(optional)</span></label>
            <input type="password" v-model="form.newPassword" class="form-control form-control-sm" placeholder="New password" autocomplete="new-password" />
          </div>
          <div class="modal-field">
            <label>Confirm new password</label>
            <input type="password" v-model="form.confirmPassword" class="form-control form-control-sm" placeholder="Confirm new password" autocomplete="new-password" />
          </div>
          <div v-if="editError" class="modal-alert error"><i class="fas fa-exclamation-circle me-1"></i>{{ editError }}</div>
          <div v-if="editSuccess" class="modal-alert success"><i class="fas fa-check-circle me-1"></i>Profile updated successfully.</div>
        </div>
        <div class="pf-modal-footer">
          <button class="btn btn-sm btn-secondary" @click="closeEdit">Cancel</button>
          <button class="btn btn-sm btn-dark" :disabled="editLoading" @click="saveProfile">
            <i v-if="editLoading" class="fas fa-spinner fa-spin me-1"></i>{{ editLoading ? 'Saving...' : 'Save' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Jira manual Account ID (fallback only) ── -->
    <div v-if="showJiraManualModal" class="modal-overlay">
      <div class="pf-modal">
        <div class="pf-modal-header">
          <h6><i class="fab fa-jira me-2" style="color:#0052CC;"></i>Link Jira by Account ID</h6>
        </div>
        <div class="pf-modal-body">
          <p class="text-secondary text-xs mb-3">
            Prefer <button type="button" class="btn btn-link btn-sm p-0 align-baseline" @click="closeJiraManualModal(); startConnectJira();">Authenticate with Jira</button>
            instead. Use this only if OAuth is unavailable.
          </p>
          <div class="modal-field">
            <label>Jira Account ID <span class="text-danger">*</span></label>
            <input type="text" v-model="jiraAccountId" class="form-control form-control-sm" placeholder="e.g. 712020:abc123..." />
          </div>
          <div v-if="jiraError" class="modal-alert error mt-2"><i class="fas fa-exclamation-circle me-1"></i>{{ jiraError }}</div>
        </div>
        <div class="pf-modal-footer">
          <button type="button" class="btn btn-sm btn-secondary" @click="closeJiraManualModal">Cancel</button>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary"
            :disabled="jiraLoading"
            @click="connectJira"
          >
            <i v-if="jiraLoading" class="fas fa-spinner fa-spin me-1"></i>{{ jiraLoading ? "Saving..." : "Save" }}
          </button>
        </div>
      </div>
    </div>

    <!-- ── Connect Google (hidden button rendered by Google SDK) ── -->
    <div id="google-connect-btn" style="display:none;"></div>
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import setNavPills from "@/assets/js/nav-pills.js";
import { roleBadgeStyle } from "@/utils/roleColors";
import { API_BASE_URL, apiHeaders, fetchWithTimeout } from "@/config/apiBase";
import { hasActiveSession } from "@/config/session";
import { signInWithGoogleAndGetIdToken } from "@/config/firebaseAuth";
import { resolveGoogleAuthMode } from "@/config/googleAuthMode";
import { formatUserTeams } from "@/utils/userTeams";

export default {
  name: "ProfileOverview",
  data() {
    return {
      showEdit: false,
      form: { firstName: "", lastName: "", email: "", nickname: "", currentPassword: "", newPassword: "", confirmPassword: "" },
      editError: null,
      editSuccess: false,
      editLoading: false,
      // Jira
      showJiraManualModal: false,
      jiraAccountId: "",
      jiraError: null,
      jiraInlineError: null,
      jiraInlineSuccess: null,
      jiraLoading: false,
      jiraConnectLoading: false,
      jiraDisconnectLoading: false,
      jiraOAuthConfigured: false,
      googleConnectLoading: false,
      useFirebaseGoogle: false,
      googleClientId: "",
    };
  },
  computed: {
    user() { return this.$store.state.user || {}; },
    initials() {
      return ((this.user.firstName?.[0] || "") + (this.user.lastName?.[0] || "")).toUpperCase() || "?";
    },
    displayFullName() {
      const fn = (this.user.firstName || "").trim();
      const ln = (this.user.lastName || "").trim();
      const nn = (this.user.nickname || "").trim();
      if (nn) return `${fn} (${nn}) ${ln}`.trim();
      return `${fn} ${ln}`.trim() || "—";
    },
    profileRoles() {
      if (this.user.roles && this.user.roles.length) return this.user.roles;
      if (this.user.roleId) return [{ roleId: this.user.roleId, roleName: this.user.roleName }];
      return [];
    },
    profileRoleIds() {
      return this.profileRoles.map((r) => r.roleId);
    },
    formattedTeam() {
      return formatUserTeams(this.user?.teams?.length ? this.user.teams : this.user?.team);
    },
  },
  async mounted() {
    const mode = await resolveGoogleAuthMode();
    this.useFirebaseGoogle = mode.useFirebase;
    this.googleClientId = mode.googleClientId;
    this.$store.state.isAbsolute = true;
    setNavPills();
    await this.loadJiraOAuthConfig();
    await this.handleJiraOAuthReturn();
  },
  beforeUnmount() {
    this.$store.state.isAbsolute = false;
  },
  methods: {
    ...mapMutations(["setUser"]),

    roleLabel(id) {
      return { 1: "QA", 2: "Dev", 3: "PM", 4: "Admin" }[id] || "—";
    },
    roleStyle(roleId, roleName) {
      return roleBadgeStyle(roleId, roleName);
    },

    // ── Edit Profile ──
    openEdit() {
      this.form = {
        firstName: this.user.firstName || "",
        lastName: this.user.lastName || "",
        email: this.user.email || "",
        nickname: this.user.nickname || "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      };
      this.editError = null;
      this.editSuccess = false;
      this.showEdit = true;
    },
    closeEdit() { this.showEdit = false; },

    async saveProfile() {
      this.editError = null;
      this.editSuccess = false;
      if (!this.form.firstName.trim() || !this.form.lastName.trim() || !this.form.email.trim()) { this.editError = "First name, last name, and email are required."; return; }
      const changingPwd = !!(this.form.newPassword || this.form.confirmPassword || this.form.currentPassword);
      if (changingPwd) {
        if (!this.form.currentPassword) { this.editError = "Enter your current password to set a new one."; return; }
        if (!this.form.newPassword) { this.editError = "Enter a new password or clear the password fields."; return; }
        if (this.form.newPassword !== this.form.confirmPassword) { this.editError = "New passwords do not match."; return; }
        if (this.form.newPassword.length < 6) { this.editError = "New password must be at least 6 characters."; return; }
      }
      this.editLoading = true;
      try {
        if (changingPwd) {
          const resPw = await fetchWithTimeout(`${API_BASE_URL}/api/auth/change-password`, {
            method: "POST",
            headers: apiHeaders({ "Content-Type": "application/json" }),
            body: JSON.stringify({
              email: this.user.email,
              currentPassword: this.form.currentPassword,
              newPassword: this.form.newPassword,
            }),
          });
          const pwData = await resPw.json();
          if (!resPw.ok) throw new Error(pwData.error || "Failed to change password");
          this.setUser({ user: { ...this.user, ...pwData.user }, token: pwData.token });
        }
        const payload = {
          firstName: this.form.firstName.trim(),
          lastName: this.form.lastName.trim(),
          nickname: this.form.nickname.trim(),
          email: this.form.email.trim(),
          team: this.user.team,
        };
        if (this.profileRoleIds.length) payload.roleIds = this.profileRoleIds;
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/users/${this.user.userId}`, { method: "PUT", headers: apiHeaders({ "Content-Type": "application/json" }), body: JSON.stringify(payload) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to update profile");
        this.setUser({
          ...this.user,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.username || this.form.email.trim(),
          name: `${data.firstName} ${data.lastName}`,
          nickname: data.nickname,
          roles: data.roles,
          roleId: data.roleId,
          roleName: data.roleName,
        });
        this.editSuccess = true;
      } catch (err) { this.editError = err.message; }
      finally { this.editLoading = false; }
    },

    async loadJiraOAuthConfig() {
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/config/web`);
        if (res.ok) {
          const data = await res.json();
          this.jiraOAuthConfigured = !!data.jiraOAuthConfigured;
        }
      } catch (_) {
        this.jiraOAuthConfigured = false;
      }
    },
    async handleJiraOAuthReturn() {
      const connected = this.$route.query.jira_connected;
      const err = this.$route.query.jira_error;
      if (!connected && !err) return;
      this.jiraInlineError = null;
      this.jiraInlineSuccess = null;
      if (err) {
        this.jiraInlineError = decodeURIComponent(String(err));
      } else if (connected) {
        await this.refreshSessionUser();
        this.jiraInlineSuccess = null;
      }
      this.$router.replace({ path: "/profile", query: {} });
    },
    async refreshSessionUser() {
      if (!this.user?.userId) return;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/auth/me`, { headers: apiHeaders() });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to refresh profile");
        if (data.user) this.setUser({ user: data.user, token: data.token });
      } catch (e) {
        console.error("refreshSessionUser:", e);
      }
    },
    openJiraManualModal() {
      this.jiraError = null;
      this.showJiraManualModal = true;
    },
    closeJiraManualModal() {
      this.showJiraManualModal = false;
      this.jiraError = null;
    },
    async startConnectJira() {
      if (!this.user?.userId) return;
      if (!hasActiveSession()) {
        this.jiraInlineError =
          "Session expired or missing sign-in token. Please sign out and sign in again, then retry Connect Jira.";
        return;
      }
      this.jiraConnectLoading = true;
      this.jiraInlineError = null;
      this.jiraInlineSuccess = null;
      this.closeJiraManualModal();
      const returnUrl = encodeURIComponent(window.location.origin);
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/auth/jira/configured`);
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.configured === false) {
          this.jiraInlineError =
            "Jira OAuth is not configured on the server. Set JIRA_OAUTH_CLIENT_ID, JIRA_OAUTH_CLIENT_SECRET, and JIRA_OAUTH_REDIRECT_URI in backend/.env, then restart the API (yarn dev:clean).";
          return;
        }
        const urlRes = await fetchWithTimeout(
          `${API_BASE_URL}/api/auth/jira/authorize-url?returnUrl=${returnUrl}`,
          { headers: apiHeaders() }
        );
        const urlData = await urlRes.json();
        if (!urlRes.ok) throw new Error(urlData.error || "Cannot start Jira connection");
        if (!urlData.url) throw new Error("Missing authorize URL");
        window.location.href = urlData.url;
      } catch (err) {
        this.jiraInlineError = err.message;
      } finally {
        this.jiraConnectLoading = false;
      }
    },

    async disconnectJira() {
      if (!this.user?.userId) return;
      if (!window.confirm("Disconnect Jira from this account?")) return;
      this.jiraDisconnectLoading = true;
      this.jiraInlineError = null;
      this.jiraInlineSuccess = null;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/auth/disconnect-jira`, {
          method: "POST",
          headers: apiHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({}),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to disconnect Jira");
        if (data.user) this.setUser(data.user);
        else this.setUser({ ...this.user, jiraAccountId: null });
      } catch (err) {
        this.jiraInlineError = err.message;
      } finally {
        this.jiraDisconnectLoading = false;
      }
    },

    // ── Connect Jira (manual account id fallback) ──
    async connectJira() {
      this.jiraError = null;
      if (!this.jiraAccountId.trim()) {
        this.jiraError = "Please enter your Jira Account ID.";
        return;
      }
      this.jiraLoading = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/auth/connect-jira`, {
          method: "POST",
          headers: apiHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ jiraAccountId: this.jiraAccountId.trim() }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to connect Jira");
        this.setUser({ ...this.user, jiraAccountId: this.jiraAccountId.trim() });
        this.closeJiraManualModal();
        this.jiraInlineSuccess = null;
      } catch (err) {
        this.jiraError = err.message;
      } finally {
        this.jiraLoading = false;
      }
    },

    // ── Connect Google ──
    async connectGoogleWithFirebase() {
      this.googleConnectLoading = true;
      try {
        const credential = await signInWithGoogleAndGetIdToken();
        await this.handleGoogleConnect({ credential });
      } catch (err) {
        alert(err.message || "Google sign-in failed");
      } finally {
        this.googleConnectLoading = false;
      }
    },
    startConnectGoogle() {
      if (this.useFirebaseGoogle) {
        return this.connectGoogleWithFirebase();
      }
      if (!this.googleClientId) {
        alert("Google sign-in is not configured.");
        return;
      }
      if (!window.google) {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.onload = () => this.triggerGooglePicker();
        document.head.appendChild(script);
      } else {
        this.triggerGooglePicker();
      }
    },
    triggerGooglePicker() {
      window.google.accounts.id.initialize({ client_id: this.googleClientId, callback: this.handleGoogleConnect });
      window.google.accounts.id.prompt();
    },
    async handleGoogleConnect({ credential }) {
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/auth/connect-google`, { method: "POST", headers: apiHeaders({ "Content-Type": "application/json" }), body: JSON.stringify({ credential }) });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to connect Google");
        this.setUser({ ...this.user, googleId: data.googleId, picture: data.picture });
      } catch (err) { alert(err.message); }
    },
  },
};
</script>

<style scoped>
/* ── Banner ── */
.profile-banner {
  height: 220px;
  background-size: cover;
  background-position: center 50%;
  position: relative;
  overflow: hidden;
}

/* ── Identity bar ── */
.identity-bar {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  background: #fff;
  border: 1px solid var(--surface-border);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-card);
  padding: 1rem 1.5rem;
  margin-top: -2.5rem;
  position: relative;
  z-index: 10;
}

.identity-avatar {
  flex-shrink: 0;
  width: 4.5rem;
  height: 4.5rem;
  border-radius: 12px;
  overflow: hidden;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);
}

.identity-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  width: 100%;
  height: 100%;
  background: var(--primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 700;
}

.identity-info {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.identity-roles {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
}

.identity-name {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.identity-role {
  display: inline-block;
  padding: 0.2rem 0.75rem;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 700;
}

/* ── Profile info grid ── */
.pf-btn-edit {
  padding: 0.4rem 1rem;
  background: var(--primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}
.pf-btn-edit:hover { opacity: 0.85; }

.pf-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  border-bottom: 1px solid var(--surface-border);
}
.pf-row.border-bottom-0 { border-bottom: none; }

.pf-cell {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 1rem 1.25rem;
  border-right: 1px solid var(--surface-border);
}
.pf-cell:last-child { border-right: none; }

.pf-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-secondary);
}

.pf-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
}

.role-pill {
  display: inline-block;
  padding: 0.2rem 0.65rem;
  border-radius: 50px;
  font-size: 0.7rem;
  font-weight: 700;
}

.method-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.65rem;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
}
.method-badge.google { background:#fce8e6; color:#d44638; }
.method-badge.password { background:#e8f0fe; color:#1a73e8; }

/* ── Connected accounts ── */
.connect-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid var(--surface-border);
}
.connect-row.border-bottom-0 { border-bottom: none; }

.connect-icon {
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  flex-shrink: 0;
}
.google-icon { background: #fce8e6; color: #d44638; }
.jira-icon   { background: #e6eeff; color: #0052CC; }

.connect-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.connect-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
}

.connect-status {
  font-size: 0.75rem;
  color: var(--text-secondary);
}
.connect-status.connected { color: #2ecc71; font-weight: 600; }

.connect-account-id {
  font-size: 0.7rem;
  color: var(--text-secondary);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  word-break: break-all;
  line-height: 1.35;
  max-width: 100%;
}

.connect-btn-disconnect {
  border-color: #e53e3e;
  color: #e53e3e;
  background: #fff;
}
.connect-btn-disconnect:hover:not(:disabled) {
  border-color: #c53030;
  color: #fff;
  background: #e53e3e;
}
.connect-btn-disconnect:disabled {
  opacity: 0.7;
  cursor: wait;
}

.connect-btn {
  padding: 0.35rem 0.9rem;
  border: 1.5px solid var(--surface-border);
  background: #fff;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-primary);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.connect-btn:hover { border-color: var(--primary); color: var(--primary); background: var(--primary-light); }

.connect-row-meta {
  padding: 0 1.25rem 0.85rem;
  border-bottom: none;
}

.connect-inline-error {
  font-size: 0.75rem;
  color: #e53e3e;
  line-height: 1.4;
}
.connect-inline-success {
  font-size: 0.75rem;
  color: #2e7d32;
  line-height: 1.4;
}
.jira-manual-link {
  font-size: 0.72rem;
  color: var(--text-secondary);
  text-decoration: none;
}
.jira-manual-link:hover {
  color: var(--primary);
  text-decoration: underline;
}

.connect-check { color: #2ecc71; font-size: 1.1rem; }

/* ── Modals ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.pf-modal {
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 440px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  overflow: hidden;
}

.pf-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
}
.pf-modal-header h6 { margin: 0; font-size: 0.95rem; font-weight: 700; }

.modal-close-btn {
  background: none;
  border: none;
  color: #adb5bd;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0;
}
.modal-close-btn:hover { color: var(--text-primary); }

.pf-modal-body {
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.modal-row { display: flex; gap: 0.75rem; }

.modal-field {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.modal-field label { font-size: 0.775rem; font-weight: 600; color: var(--text-secondary); }

.pf-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 0.9rem 1.5rem 1.25rem;
}

.modal-alert {
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
  font-size: 0.8rem;
}
.modal-alert.error   { background:#fff5f5; border:1px solid #fed7d7; color:#c53030; }
.modal-alert.success { background:#f0fff4; border:1px solid #c6f6d5; color:#276749; }
</style>
